import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { translate, translateForLocations, resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import record, { flatten, deepen, getTopIndex } from '../../versa/record.js';
import dialog from '../../versa/dialog.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany, resolvePrinterDeviceId } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/outputs.js';
import form from '../forms/outputs.js';
import buildCreateInvoicesForm, { buildOutputPurposeInnerHtml } from '../forms/create-invoices.js';
import import_picklist_form from '../forms/outputs-import-picklist.js';
import print_labels_form from '../forms/outputs-labels.js';
import outputs_results_view from '../views/outputs-results.js';
import reverse_form from '../forms/outputs-reverse.js';
import * as types from '../types.js';

/** Modal: escolhe layout padrão (QR JSON) ou SESSP (QR nNF|vol|total). */
function pickVolumeLabelLayout(onPick)
{
	const html = `
		<div class="msgbox">
			<div class="mb-title">Layout da etiqueta</div>
			<div class="mb-text">
				<p style="margin-bottom:0.75rem">Escolha o modelo de impressão.</p>
				<label class="volume-label-layout-opt" style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="volume_label_layout" value="default" checked style="margin-top:0.2rem">
					<span><strong>Padrão</strong> — mesmo layout visual de sempre; QR em JSON (empresa, saída e volume).</span>
				</label>
				<label class="volume-label-layout-opt" style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="volume_label_layout" value="sessp" style="margin-top:0.2rem">
					<span><strong>SESSP</strong> — modelo IBL (produto/lote do 1º item, destinatário, bloco DRS para uso futuro) e QR <code>nº&nbsp;NF|volume|total</code> (ex.: <code>2925194|2|10</code>). Exige <strong>NF-e emitida</strong> para a saída.</span>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Continuar</button>
		</div>
	`;
	const dlg = dialog.create(html, (key) =>
	{
		if (key === 'Escape' || key === 'N')
		{
			dlg.destroy();
			return;
		}
		if (key === 'Y')
		{
			const r = dlg.getNode().querySelector('input[name="volume_label_layout"]:checked');
			const layout = r && r.value ? r.value : 'default';
			dlg.destroy();
			onPick(layout);
		}
	}, /Enter|Escape|N|Y/);
}

function downloadPalletsExport(base64Content, filename, mimeType)
{
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(url);
}

/** Modal: PDF em grade (atual) ou tabela CSV/Excel com filtro de data opcional. */
function pickPalletsOverturnFormat(count, onPick)
{
	const html = `
		<div class="msgbox">
			<div class="mb-title">Derrubada de Pallets</div>
			<div class="mb-text">
				<p style="margin-bottom:0.75rem"><strong>${count}</strong> saída(ões) selecionada(s).</p>
				<p style="margin-bottom:0.5rem">Formato do relatório:</p>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="po_layout" value="grade" checked style="margin-top:0.2rem">
					<span><strong>GRADE — PDF</strong> — grade de posições fora de alcance (comportamento atual).</span>
				</label>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="po_layout" value="tabela" style="margin-top:0.2rem">
					<span><strong>Tabela — CSV ou Excel</strong> — linhas por item (posição, categoria térmica/SKU/produto/lote, fatura, quantidade).</span>
				</label>
				<div id="pallets-overturn-table-opts" style="display:none;margin-top:0.75rem;padding-top:0.75rem;border-top:1px solid #ddd">
					<p style="margin-bottom:0.5rem;font-size:0.9rem"><strong>Filtro de data</strong> (opcional — data de criação da saída; deixe em branco para todas as selecionadas)</p>
					<div style="display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:0.75rem">
						<label>De <input type="date" id="po-date-from" autocomplete="off"></label>
						<label>Até <input type="date" id="po-date-to" autocomplete="off"></label>
					</div>
					<p style="margin-bottom:0.35rem">Arquivo:</p>
					<label style="margin-right:0.75rem;cursor:pointer"><input type="radio" name="po_export_fmt" value="csv" checked> CSV</label>
					<label style="cursor:pointer"><input type="radio" name="po_export_fmt" value="xlsx"> Excel (.xlsx)</label>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Gerar</button>
		</div>
	`;
	const dlg = dialog.create(html, (key) =>
	{
		if (key === 'Escape' || key === 'N')
		{
			dlg.destroy();
			return;
		}
		if (key === 'Y')
		{
			const node = dlg.getNode();
			const lr = node.querySelector('input[name="po_layout"]:checked');
			const layout = lr && lr.value ? lr.value : 'grade';
			let dateFrom = '';
			let dateTo = '';
			let format = 'csv';
			if (layout === 'tabela')
			{
				dateFrom = (node.querySelector('#po-date-from') || {}).value || '';
				dateTo = (node.querySelector('#po-date-to') || {}).value || '';
				if (dateFrom && dateTo && dateFrom > dateTo)
				{
					notify('Data inicial não pode ser maior que a final.', 'Filtro de data');
					return;
				}
				const rf = node.querySelector('input[name="po_export_fmt"]:checked');
				format = rf && rf.value ? rf.value : 'csv';
			}
			dlg.destroy();
			onPick({ layout, format, date_from: dateFrom || undefined, date_to: dateTo || undefined });
		}
	}, /Enter|Escape|N|Y/);
	const node = dlg.getNode();
	const box = node.querySelector('#pallets-overturn-table-opts');
	const syncOpts = () =>
	{
		const r = node.querySelector('input[name="po_layout"]:checked');
		const layout = r && r.value ? r.value : 'grade';
		if (box) box.style.display = layout === 'tabela' ? 'block' : 'none';
	};
	node.querySelectorAll('input[name="po_layout"]').forEach((el) => el.addEventListener('change', syncOpts));
	syncOpts();
}

const outputs = function()
{

	let outputs_crud;

	function bindDeleteSeparateLinesUI(fs)
	{
		const root = fs.dialog.getNode();
		const btn = root.querySelector('#bn-delete-separate-output-lines');
		const statusEl = root.querySelector('#status');
		if (!btn)
			return;
		const syncVisibility = () =>
		{
			const raw = fs.form.getFieldBySelector('#status')?.getValue?.();
			const st = raw != null && raw !== '' ? Number(raw) : NaN;
			const ok = st === 30;
			btn.style.display = ok ? 'inline-block' : 'none';
			btn.disabled = !ok;
		};
		syncVisibility();
		if (statusEl)
			statusEl.addEventListener('change', syncVisibility);
		btn.onclick = async () =>
		{
			const indices = fs.getSelectedIndices('.row');
			if (!indices.length)
			{
				notify('Marque as linhas a remover (checkbox à esquerda).', 'Saídas');
				return;
			}
			const company_id = fs.form.getFieldBySelector('#company_id').getValue();
			const output_id = Number(fs.form.getFieldBySelector('#id').getValue());
			if (!company_id || !output_id)
			{
				notify('Saída ou empresa inválida.', 'Saídas');
				return;
			}
			const nums = [];
			for (const idx of indices)
			{
				const el = root.querySelector(`#number-${idx}`);
				const n = el && el.value;
				if (n != null && String(n).trim() !== '' && Number.isFinite(Number(n)))
					nums.push(Number(n));
			}
			if (!nums.length)
			{
				notify('Não foi possível ler o número das linhas (feche e abra o registro novamente).', 'Saídas');
				return;
			}
			ask(
				`Remover ${nums.length} linha(s) no servidor (${nums.join(', ')})? Se houver reserva, ela volta ao estoque livre (R→L).`,
				'Confirmar remoção',
				async (answer) =>
				{
					if (answer !== 'Y')
						return;
					const resp = await api.call(api.delete_output_separate_lines, company_id, output_id, { item_numbers: nums });
					if (!resp)
						return;
					notify(`Linhas removidas: ${nums.join(', ')}.`, 'Saídas');
					const flat = await outputs_crud.loadRecord(JSON.stringify([company_id, output_id]));
					const top = getTopIndex(flat);
					let have = fs.getTableSize();
					while (have > top + 1)
					{
						fs.removeRows([have - 1], '.row');
						have--;
					}
					while (have < top + 1)
					{
						fs.appendRows(1, '.row');
						have++;
					}
					fs.form.setValues(flat);
					fs.form.refresh();
					setTimeout(() => fs.form.refresh(), 200);
					syncVisibility();
				}
			);
		};
	}

	const record_output_form = {
		...form,
		form: {
			...form.form,
			afterEditSetValues(fs)
			{
				bindDeleteSeparateLinesUI(fs);
			},
		},
	};

	outputs_crud = crud.create({
		/** Posições no IndexedDB por empresa do remetente (todas as filiais), para rótulo `endereço · arm. N` em linhas multi-armazém. */
		async queryLocationsWideForIndexedDb()
		{
			const params = new URLSearchParams();
			let f = this.filter;
			if (f && Object.keys(f).some(k => /^field-\d+$/.test(k)))
				f = await resolveFilterLabelsToIds(f, this.fieldspecs);
			let expr = translate(f, ['company_id'], this.fieldspecs);
			if (!expr)
				expr = translateForLocations(f, this.fieldspecs);
			if (expr)
				params.append('filter', expr);
			params.append('sort', '4');
			params.append('limit', '0');
			return '?' + params.toString();
		},
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_locations, await this.queryLocationsWideForIndexedDb()).then((data) => db.set('locations', data).catch(error => notify(error)));
			api.call(api.list_products, this.queryAll()).then((data) => db.set('products', data).catch(error => notify(error)));
			api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));
			api.call(api.list_devices, this.queryAll()).then((data) => db.set('devices', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_outputs, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_output, ...JSON.parse(id));
			const flat = flatten(_record, 'items');
			const hdrWh = _record.warehouse_id;
			const top = getTopIndex(flat);
			for (let i = 0; i <= top; i++)
			{
				const lid = flat[`location_id-${i}`];
				const wline = flat[`warehouse_id-${i}`] ?? hdrWh;
				if (lid != null && lid !== '' && wline != null && wline !== '' && !String(lid).includes(':'))
					flat[`location_id-${i}`] = `${wline}:${lid}`;
			}
			const rem = _record.warehouse_company_id;
			if (rem)
			{
				const qs = new URLSearchParams({
					filter: `company_id*eq*${rem}`,
					sort: '4',
					limit: '0',
				}).toString();
				try
				{
					const locs = await api.call(api.list_locations, '?' + qs);
					if (Array.isArray(locs) && locs.length)
						await db.set('locations', locs);
				}
				catch (_) { /* offline / permissão: datalist pode ficar só com o que já havia no IDB */ }
			}
			return flat;
		},
		editRecord(data)
		{
			record.edit(record_output_form, this.saveRecordWrapper.bind(this), data, false);	// false = turn off autorow for this module
		},
		async editFilter() {
			api.call(api.list_locations, await this.queryLocationsWideForIndexedDb()).then((data) => db.set('locations', data)).catch(() => {});
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => {
					const resolved = await resolveFilterLabelsToIds(formData, this.fieldspecs);
					const parts = [];
					for (let i = 0; i < 10; i++) {
						const f = resolved[`field-${i}`];
						const v = resolved[`value-${i}`];
						if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
						else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
						else if (f === 'warehouse_company_id' && v) parts.push(`company_id*eq*${v}`);
					}
					if (parts.length === 0) return;
					const params = new URLSearchParams();
					params.append('filter', parts.join(','));
					params.append('sort', '4');
					params.append('limit', '0');
					const data = await api.call(api.list_locations, '?' + params.toString());
					if (data && Array.isArray(data)) db.set('locations', data);
				}
			});
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.filterExplicitlyApplied = true;
			api.call(api.list_locations, await this.queryLocationsWideForIndexedDb()).then((data) => db.set('locations', data)).catch(() => {});
			await this.loadPage(1);
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			if (!data.items)	// Invalid order number used
				return;
			let _record = {
				order_type: data.order_type,
				order_id: Number(data.order_id),
				warehouse_company_id: data.warehouse_company_id,
				warehouse_id: Number(data.warehouse_id),
				volume_type: data.volume_type ?? null,
				volumes: Number(data.volumes),
				weight: Number(data.weight),
				status: Number(data.status),
				items: data.items
				.filter(item => item && (item.product_id != null && item.product_id !== '') && Number(item.product_id) > 0)
				.map(item =>
				{
					let locRaw = item.location_id;
					let whLine = item.warehouse_id;
					const locStr = String(locRaw ?? '').trim();
					if (/^\d+:\d+$/.test(locStr))
					{
						const [a, b] = locStr.split(':').map(Number);
						whLine = whLine ?? a;
						locRaw = b;
					}
					if (whLine == null || whLine === '')
						whLine = data.warehouse_id;
					const numRaw = item.number;
					const out = {
						location_id: Number(locRaw),
						warehouse_id: whLine != null && whLine !== '' ? Number(whLine) : null,
						product_id: Number(item.product_id),
						lot_id: item.lot_id > 0 ? Number(item.lot_id) : null,
						quantity_required: Number(item.quantity_required),
						quantity_supplied: Number(item.quantity_supplied),
						discount: Number(item.discount),
						unit_cost: 0,	// These numbers will be recalculated at the backend
						unit_price: 0
					};
					if (numRaw != null && String(numRaw).trim() !== '' && Number.isFinite(Number(numRaw)))
						out.number = Number(numRaw);
					return out;
				})
			};
			if (old_data)
				return await api.call(api.update_output, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_output, data.company_id, _record);
		},
		cancelOutputs()
		{
			const showResult = async (results) =>
			{
				notify(await outputs_results_view(results), 'Resultados');
				this.loadPage();
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.cancel_outputs, { 'ids': ids }).then(showResult);
			}
			ask(`Cancelar reservas de ${this.getCount()} saídas?`, 'Cancelar Saídas', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async createInvoices()
		{
			const showResult = (resp) =>
			{
				this.clearGridSelection();
				notify(`Notas criadas: ${resp?.count || 0}`, 'Resultado');
				fs.destroy();
			};
			const sendRequest = async (data) =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const purposeRaw = String(data.purpose ?? '').trim();
				const payload = {
					io_type: 'O',
					ids,
					info: data.info,
					include: data.include,
				};
				if (/^[1-6]$/.test(purposeRaw))
					payload.purpose = purposeRaw;
				else if (purposeRaw)
					payload.cfop = purposeRaw;
				else
					throw new Error('Selecione uma finalidade ou um CFOP cadastrado.');
				await api.call(api.create_invoices, payload).then(showResult);
			};
			let formDef;
			try
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const companies = new Set(ids.map((r) => r[0]));
				if (companies.size === 1)
				{
					const companyId = [...companies][0];
					const rows = await api.call(api.list_cfops_by_company, companyId);
					formDef = buildCreateInvoicesForm(false, buildOutputPurposeInnerHtml(rows));
				}
				else
					formDef = buildCreateInvoicesForm(false);
			}
			catch (e)
			{
				notify(String(e.message || e), 'Erro');
				return;
			}
			let fs = formset.create(formDef, sendRequest);
			fs.init = (() =>
			{
				const n = this.getCount();
				document.querySelector(`${fs.form.selector} #count`).innerHTML = n;
				const noun = document.querySelector(`${fs.form.selector} #io-noun`);
				if (noun)
					noun.textContent = n === 1 ? 'saída' : 'saídas';
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		closeOutputs()
		{
			const showResult = async (results) =>
			{
				notify(await outputs_results_view(results), 'Resultados');
				this.loadPage();
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.close_outputs, { 'ids': ids }).then(showResult);
			}
			ask(`Dar baixa de ${this.getCount()} saídas?`, 'Baixar Estoques', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPickControl()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_pick_control, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório?`, 'Controle de Picking', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPalletsOverturn()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.csv)
					downloadPalletsExport(resp.csv, resp.filename || 'derrubada-pallets.csv', 'text/csv;charset=utf-8');
				else if (resp?.xlsx)
					downloadPalletsExport(resp.xlsx, resp.filename || 'derrubada-pallets.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
			};
			const sendRequest = async (opts) =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				const body = { ids, layout: opts.layout || 'grade' };
				if (body.layout === 'tabela')
				{
					body.format = opts.format || 'csv';
					if (opts.date_from) body.date_from = opts.date_from;
					if (opts.date_to) body.date_to = opts.date_to;
				}
				await api.call(api.print_pallets_overturn, body).then(showResult);
			};
			const n = this.getCount();
			if (!n)
			{
				notify('Selecione ao menos uma saída.', 'Derrubada de Pallets');
				return;
			}
			pickPalletsOverturnFormat(n, (opts) => { sendRequest.call(this, opts); });
		},
		printPickLists()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_picklists, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório?`, 'Pick List', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printAvisoRemessa()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_aviso_remessa, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no Aviso de Remessa?`, 'Aviso de Remessa', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printConference()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_conference, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório de conferência?`, 'Conferência de Saída', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async printLabels()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'outputs');
				const device_id = resolvePrinterDeviceId(data.device_id, '#output-labels #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				let layout = data.label_layout != null && String(data.label_layout).trim() !== ''
					? String(data.label_layout).trim().toLowerCase()
					: 'default';
				if (layout !== 'default' && layout !== 'sessp')
					layout = 'default';
				let _record = {
					device_company_id: data.device_company_id,
					device_id,
					return_zpl: !!data.use_local_agent,
					label_layout: layout,
					outputs: data.outputs.map(output => ({ 
						company_id: output.company_id,
						output_id: Number(output.output_id),
						first_volume: Number(output.first_volume),
						last_volume: Number(output.last_volume)
					}))
				};
				const result = await api.call(api.print_output_labels, _record);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			const editVolumes = async (data, labelLayout) =>
			{
				this.clearGridSelection();
				let _record = {
					orders: data,
					label_layout: labelLayout && String(labelLayout).trim() !== '' ? String(labelLayout).trim().toLowerCase() : 'default',
				};
				record.edit(print_labels_form, sendRequest, flatten(_record, 'orders'));
			}
			const getVolumes = async (labelLayout) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id'])
				await api.call(api.list_output_volumes, { 'ids': ids }).then((d) => editVolumes(d, labelLayout));
			}
			ask(`Incluir ${this.getCount()} saídas na impressão?`, 'Etiquetas de Volumes', (answer) =>
			{
				if (answer == 'Y')
					pickVolumeLabelLayout((layout) => { getVolumes(layout); });
			});
		},
		async reverseOutput()
		{
			const sendRequest = async (data) =>
			{
				let resp = await api.call(api.reverse_output, data.company_id, data.output_id, { status: Number(data.status) });
				if (resp)
					this.loadPage();	// Show updated status
				return resp;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma saída para estornar', 'Estorno de Saída');
			record.edit(reverse_form, sendRequest, { company_id: ids[0][0], output_id: ids[0][1] });
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Saída', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador da saída' },
		{ index: 4, field: 'order_id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Número do pedido vinculado' },
		{ index: 15, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa da saída', store: 'programs', labels: ['name'] },
		{ index: 16, field: 'reference', label: 'Referência', types: [types.name()], placeholder: '', title: 'Referência do pedido' },
		{ index: 3, field: 'order_type', label: 'Origem', types: [types.opt_order_type()], placeholder: '', title: 'Origem do pedido vinculado' },
		{ index: 5, field: 'created_on', label: 'Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da criação da saída' },
		{ index: 6, field: 'expedited_on', label: 'Expedição', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de expedição da saída' },
		{ index: 7, field: 'warehouse_company_id', label: 'Remetente', types: [types.datalist()], placeholder: '', title: 'Empresa do armazém de coleta', store: 'companies', labels: ['nick'] },
		{ index: 8, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém de coleta', store: 'warehouses', labels: ['name'] },
		{ index: 9, field: 'volume_type', label: 'Tipo Volume', types: [types.opt_volume_type(), types.datalist()], placeholder: '', title: 'Tipo de volume' },
		{ index: 10, field: 'volumes', label: 'Volumes', types: [types.number(0, 2)], placeholder: '0', title: 'Número de volumes' },
		{ index: 11, field: 'weight', label: 'Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso total dos volumes' },
		{ index: 12, field: 'total_cost', label: 'Custo Total', types: [types.number(2)], placeholder: '0,00', title: 'Custo total da saída' },
		{ index: 13, field: 'total_price', label: 'Preço Total', types: [types.number(2)], placeholder: '0,00', title: 'Preço total da saída' },
		{ index: 14, field: 'status', label: 'Status', types: [types.opt_output_status(), types.datalist()], placeholder: '', title: 'Status da saída' },
		{ index: 18, field: 'lines_sku_summary', label: 'SKU (itens)', types: [], placeholder: 'trecho do SKU', title: 'Busca nos SKUs das linhas da saída (operador Contém recomendado)' },
		{ index: 19, field: 'lines_product_summary', label: 'Produto (itens)', types: [], placeholder: 'nome do produto', title: 'Busca nos nomes dos produtos das linhas (operador Contém recomendado)' },
	];

	let operations = [
		{ hash: '#cancel-outputs',	label: 'Cancelar&nbsp;Reservas'			},
		{ hash: '#create-invoices',	label: 'Gerar&nbsp;notas&nbsp;fiscais'	},
		{ hash: '#close-outputs',	label: 'Baixar&nbsp;Estoques'			},
		{ hash: '#reverse-output',	label: 'Estornar&nbsp;Saída'			},
	];

	let reports = [
		{ hash: '#print-control', 	label: 'Controle&nbsp;de&nbsp;Picking'	},
		{ hash: '#print-pallets', 	label: 'Derrubada&nbsp;de&nbsp;Pallets'	},
		{ hash: '#print-picklists',	label: 'Pick&nbsp;Lists' 				},
		{ hash: '#print-ar',		label: 'Aviso&nbsp;de&nbsp;Remessa'	},
		{ hash: '#print-conference',	label: 'Conferência&nbsp;de&nbsp;Saída'	},
		{ hash: '#print-labels',	label: 'Etiquetas&nbsp;de&nbsp;Volumes'	},
	];

	router.set('#cancel-outputs', 	outputs_crud.cancelOutputs.bind(outputs_crud));
	router.set('#create-invoices', 	outputs_crud.createInvoices.bind(outputs_crud));
	router.set('#close-outputs', 	outputs_crud.closeOutputs.bind(outputs_crud));
	router.set('#reverse-output',	outputs_crud.reverseOutput.bind(outputs_crud));

	router.set('#print-control', outputs_crud.printPickControl.bind(outputs_crud));
	router.set('#print-pallets', outputs_crud.printPalletsOverturn.bind(outputs_crud));
	router.set('#print-picklists', outputs_crud.printPickLists.bind(outputs_crud));
	router.set('#print-ar', outputs_crud.printAvisoRemessa.bind(outputs_crud));
	router.set('#print-conference', outputs_crud.printConference.bind(outputs_crud));
	router.set('#print-labels', outputs_crud.printLabels.bind(outputs_crud));

	outputs_crud.run(
		'outputs',
		'Saídas',
		document.getElementById('workarea'),
		view,
		record_output_form,
		fieldspecs,
		operations,
		reports
	);

};

export default outputs;

